import { Box, Theme, Typography } from '@mui/material';
import { useMediaQuery } from '@mui/system';
import { isBefore } from 'date-fns';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ComplianceTypes, Routes } from '@/constants';
import { Link } from '@/containers/Link';
import { useAccessInfo } from '@/hooks';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import { selectIsFreeTrialUser, selectUserCompliance } from '@/store';

export const RemainingUsage = () => {
  const planDetails = useAccessInfo();
  const isFreeTrial = useSelector(selectIsFreeTrialUser);
  const compliance = useSelector(selectUserCompliance);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const navigate = useNavigate();

  if (!planDetails) return null;

  const onViewPricing = () => {
    AnalyticsService.pageViewPricing({
      location: {
        component: EVENT_CONSTANTS.COMPONENT.NAV_BAR,
        button: 'Free Trial - See Pricing',
      },
    });
  };

  const navigateToPricing = () => {
    onViewPricing();
    navigate(Routes.PRICING);
  };

  const {
    showExpiry,
    showUsage,
    usageTotal,
    usageRemaining,
    usageUnits,
    daysRemaining,
    expiryDate,
    licenceValid,
  } = planDetails;

  if (isFreeTrial) {
    return (
      <Box
        sx={styles.banner}
        onClick={isMobile ? navigateToPricing : undefined}>
        {licenceValid ? (
          <>
            {!R.isNil(usageRemaining) && (
              <Box sx={styles.container}>
                <Typography variant="caption1" color="text.contrast">
                  You have
                </Typography>
                <Typography variant="caption1Bold" color="text.contrast">
                  {usageRemaining}/{usageTotal}
                </Typography>
                <Typography variant="caption1" color="text.contrast">
                  free{' '}
                  {compliance === ComplianceTypes.CPE ? 'credits' : 'minutes'}{' '}
                  left.
                </Typography>
              </Box>
            )}
            {!R.isNil(daysRemaining) && (
              <Box sx={styles.container}>
                <Typography variant="caption1" color="text.contrast">
                  Expires in
                </Typography>
                <Typography variant="caption1Bold" color="text.contrast">
                  {daysRemaining}
                </Typography>
                <Typography variant="caption1" color="text.contrast">
                  day
                  {daysRemaining === 1 ? '' : 's'}.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box sx={styles.container}>
              <Typography variant="caption1" color="text.contrast">
                Your
              </Typography>
              <Typography variant="caption1Bold" color="text.contrast">
                free trial has ended.
              </Typography>
            </Box>
            <Box sx={styles.container}>
              <Typography variant="caption1" color="text.contrast">
                Upgrade now to continue enjoying LumiQ.
              </Typography>
            </Box>
          </>
        )}
        <Link onClick={onViewPricing} to={Routes.PRICING}>
          <Typography
            variant="body3"
            sx={{
              color: 'text.contrast',
              textDecoration: 'underline',
              ml: 1,
              pb: '2px',
              display: { xs: 'none', md: 'block' },
            }}>
            {licenceValid ? 'See Pricing →' : 'Upgrade Now →'}
          </Typography>
        </Link>
      </Box>
    );
  }

  const isExpired =
    showExpiry && !!expiryDate && isBefore(new Date(expiryDate), new Date());
  const isEmpty = showUsage && !usageRemaining;

  if (!licenceValid) {
    return (
      <Box
        sx={styles.banner}
        onClick={isMobile ? navigateToPricing : undefined}>
        {showUsage && (!isExpired || isEmpty) && (
          <Box sx={styles.container}>
            <Typography variant="caption1Bold" color="text.contrast">
              You have
            </Typography>
            <Typography variant="body3SemiBold" color="text.contrast">
              {`${usageRemaining} ${usageUnits}`}
            </Typography>
            <Typography variant="caption1Bold" color="text.contrast">
              remaining.
            </Typography>
          </Box>
        )}
        {showExpiry && !isEmpty && (
          <Box sx={styles.container}>
            <Typography variant="caption1" color="text.contrast">
              {isExpired
                ? 'Your subscription expired on'
                : 'Your subscription is valid until'}
            </Typography>
            <Typography variant="caption1Bold" color="text.contrast">
              {expiryDate}.
            </Typography>
          </Box>
        )}
        <Link onClick={onViewPricing} to={Routes.PRICING}>
          <Typography
            variant="body3"
            sx={{
              color: 'text.contrast',
              textDecoration: 'underline',
              ml: 1,
              pb: '2px',
              display: { xs: 'none', md: 'block' },
            }}>
            {licenceValid ? 'See Pricing →' : 'Upgrade Now →'}
          </Typography>
        </Link>
      </Box>
    );
  }

  return null;
};

const styles = {
  banner: {
    height: ({ dimensions }: Theme) => dimensions.navBanner.height,
    bgcolor: 'secondary.main',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: 0, md: 0.5 },
    display: 'flex',
  },
  container: {
    display: 'flex',
    gap: 0.5,
    alignItems: 'center',
  },
};
